import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { MobileList } from '@/components/ui/mobile-list'

/* one card for two tables: the platform invoice list and the stripe billing
 * history. the caller formats its own row, so neither shape leaks in here. */
export type InvoiceMobileRow = {
  id: string
  href?: string
  title: string
  subtitle?: string
  meta?: ReactNode
  amount: string
  badge?: ReactNode
  action?: ReactNode
}

export function InvoiceMobileList({
  items,
  rowLinkState,
  isLoading = false,
  emptyMessage
}: {
  items: InvoiceMobileRow[]
  rowLinkState?: unknown
  isLoading?: boolean
  emptyMessage: string
}) {
  return (
    <MobileList
      isLoading={isLoading}
      isEmpty={items.length === 0}
      emptyMessage={emptyMessage}
    >
      {items.map(row => (
        <li key={row.id} className='flex items-start gap-3 px-4 py-3'>
          {/* the action sits outside the link: a link inside a link is not
              clickable on its own. */}
          <RowBody href={row.href} state={rowLinkState}>
            <div className='flex items-baseline justify-between gap-3'>
              <span className='truncate text-[13px] font-medium text-ink'>
                {row.title}
              </span>
              <span className='shrink-0 text-[13px] font-medium tabular-nums text-ink'>
                {row.amount}
              </span>
            </div>
            {row.subtitle ? (
              <span className='truncate text-[12px] text-ink-soft'>
                {row.subtitle}
              </span>
            ) : null}
            {row.meta ? (
              <span className='text-[11px] text-ink-muted'>{row.meta}</span>
            ) : null}
            {row.badge ? (
              <div className='flex flex-wrap items-center gap-2 pt-1'>
                {row.badge}
              </div>
            ) : null}
          </RowBody>

          {row.action ? (
            <div className='shrink-0 self-start'>{row.action}</div>
          ) : null}
        </li>
      ))}
    </MobileList>
  )
}

function RowBody({
  href,
  state,
  children
}: {
  href?: string
  state?: unknown
  children: ReactNode
}) {
  const className = 'flex min-w-0 flex-1 flex-col gap-1.5'
  if (!href) return <div className={className}>{children}</div>
  return (
    <Link
      to={href}
      state={state}
      className={`${className} text-inherit no-underline`}
    >
      {children}
    </Link>
  )
}
